/* eslint-disable arrow-body-style */
import {inject} from "@angular/core";
import {Actions, createEffect, ofType} from "@ngrx/effects";
import {Store} from "@ngrx/store";
import {catchError, exhaustMap, filter, forkJoin, from, map, mergeMap, of, switchMap} from "rxjs";
import {Asset, EMPTY_ASSET} from "../../models/asset/asset";
import {AssetService} from "../../services/asset.service";
import {
    AssetActions,
    AssetCommentaryHistoryActions,
    AssetDescriptionActions,
    AssetFinancialHistoryActions,
    AssetGeneralInformationActions,
    AssetListActions,
    AssetNameActions,
    AssetOverviewActions,
    AssetSwotActions,
    AssetValuationActions,
    AssetValuationDetailActions,
    DevelopmentsSinceInvestmentActions,
    EndMarketsCustomersActions,
    FinancialPerformanceRecordsActions,
    GeneralPartnerValuationActions,
    RecentFinancialPerformanceActions,
    ShareholderActions,
    TransactionSummaryActions
} from "../asset.actions";
import {
    selectAssetFiscalYearEnd,
    selectAssetWebsiteUrl,
    selectAssetName,
    selectAssetOverviewParams,
    selectAssetState,
    selectGeneralPartnerId,
    selectGeneralPartnerValuation,
    selectRelatedFund,
    selectRelatedFundValuationId,
    selectRelatedReportId, selectReportDate,
    selectSelectedAsset,
    selectSelectedAssetId
} from "./asset.selectors";
import {FundInvestmentService} from "../../../fund/services/fund-investment.service";
import {selectSelectedSourceDataset} from "../../../import/store/import.selectors";
import {tap} from "rxjs/operators";
import {MatSnackBar} from "@angular/material/snack-bar";
import {Router} from "@angular/router";
import {PeriodUtil} from "../../../shared/utils/period-util";
import {selectActiveAssetValuation, selectActiveScenarios} from "../asset-valuation/asset-valuation.selectors";
import {AssetValuationTypeEnum} from "../../models/asset-valuation/asset-valuation-type.enum";
import {isOfScenarioType} from "../../../shared/model/scenario-type";
import {ErrorHandlingUtil} from "../../../shared/utils/error-handling-util";
import {summarizeBulkResults, toBulkResult} from "../../../shared/utils/bulk-operation-util";
import {concatLatestFrom} from "@ngrx/operators";
import {selectSelectedFundReportId} from "../../../fund/store/fund-report/fund-report.selectors";

export const loadAssetList = createEffect(
    (store$ = inject(Store), actions$ = inject(Actions), assetService = inject(AssetService)) => {
        return actions$.pipe(
            ofType(AssetListActions.load),
            concatLatestFrom(() => store$.select(selectSelectedSourceDataset)),
            exhaustMap(([action, dataset]) =>
                assetService.getAssets(dataset, action.archived).pipe(
                    map((assets) => AssetListActions.loadSuccess({assets})),
                    catchError((errorResponse: any) => of(AssetActions.loadError({errorMsg: ErrorHandlingUtil.getErrorMessage(errorResponse)})))
                )
            )
        );
    },
    {functional: true}
);

export const triggerSearchOnParamsChange = createEffect(
    (actions$ = inject(Actions)) => {
        return actions$.pipe(
            ofType(
                AssetOverviewActions.changePage,
                AssetOverviewActions.changeSort,
                AssetOverviewActions.changeSearch,
                AssetOverviewActions.changeArchived
            ),
            map(() => AssetOverviewActions.search())
        );
    },
    {functional: true}
);

export const searchAssets = createEffect(
    (store$ = inject(Store), actions$ = inject(Actions), assetService = inject(AssetService)) => {
        return actions$.pipe(
            ofType(AssetOverviewActions.search),
            concatLatestFrom(() => [
                store$.select(selectAssetOverviewParams),
                store$.select(selectSelectedSourceDataset)
            ]),
            switchMap(([, params, dataset]) =>
                assetService.searchAssets(dataset, params).pipe(
                    map((result) => AssetOverviewActions.searchSuccess({result})),
                    catchError((errorResponse: any) => of(AssetOverviewActions.searchError({errorMsg: ErrorHandlingUtil.getErrorMessage(errorResponse)})))
                )
            )
        );
    },
    {functional: true}
);

// When a search lands on a page past the end (e.g. after archiving every row on the last page),
// the backend returns empty content with a number past totalPages. Clamp back to the last real page.
export const clampAssetOverviewPageWhenOutOfRange = createEffect(
    (actions$ = inject(Actions)) => {
        return actions$.pipe(
            ofType(AssetOverviewActions.searchSuccess),
            filter(({result}) => result.content.length === 0 && result.totalElements > 0 && result.number > result.totalPages - 1),
            map(({result}) => AssetOverviewActions.changePage({page: result.totalPages - 1, size: result.size}))
        );
    },
    {functional: true}
);

export const showSearchError = createEffect(
    (actions$ = inject(Actions), snackBar = inject(MatSnackBar)) => {
        return actions$.pipe(
            ofType(AssetOverviewActions.searchError),
            tap((action) => {
                snackBar.open("Assets could not be loaded. " + action.errorMsg, "", {
                    duration: 3000
                });
            })
        );
    },
    {functional: true, dispatch: false}
);

export const createAsset = createEffect(
    (store$ = inject(Store), actions$ = inject(Actions), assetService = inject(AssetService)) => {
        return actions$.pipe(
            ofType(AssetActions.create, AssetActions.createInFundInvestmentDialog),
            concatLatestFrom(() => store$.select(selectSelectedSourceDataset)),
            exhaustMap(([action, dataset]) => {
                return assetService.saveAsset({
                    ...EMPTY_ASSET,
                    ...{
                        name: {
                            ...EMPTY_ASSET.name,
                            text: action.assetName
                        },
                        isArchived: {
                            ...EMPTY_ASSET.isArchived,
                            value: false
                        }
                    }
                }, undefined, dataset).pipe(
                    map((asset) => AssetActions.createSuccess({asset, preventNavigation: action.preventNavigation})),
                    catchError((errorResponse: any) => of(AssetActions.loadError({errorMsg: ErrorHandlingUtil.getErrorMessage(errorResponse)})))
                );
            })
        );
    },
    {functional: true}
);

export const navigateToAssetDetailsPageAfterCreate = createEffect(
    (actions$ = inject(Actions), router$ = inject(Router)) => {
        return actions$.pipe(
            ofType(AssetActions.createSuccess),
            tap((action) => {
                if (!action.preventNavigation) {
                    router$.navigate(["/assets", action.asset.id])
                }
            }));
    },
    {functional: true, dispatch: false}
);

export const reloadAssetListAfterCreatingAssetInFundInvestmentDialog = createEffect(
    (actions$ = inject(Actions)) => {
        return actions$.pipe(
            ofType(AssetActions.createSuccess),
            map((action) => action.preventNavigation
                ? AssetListActions.load({archived: false})
                : AssetListActions.doNothing()
            ));
    },
    {functional: true}
);

export const loadAssetValuationsAfterAssetOpen = createEffect(
    (actions$ = inject(Actions)) => {
        return actions$.pipe(
            ofType(AssetActions.loadAssetData),
            map((action) => action.fundValuationId
                ? AssetValuationActions.loadAllForAssetAndFundValuation({assetId: action.assetId, fundValuationId: action.fundValuationId})
                : AssetValuationActions.doNothing()
            ));
    },
    {functional: true}
);

export const loadAssetData = createEffect(
    (store$ = inject(Store), actions$ = inject(Actions), assetService = inject(AssetService)) => {
        return actions$.pipe(
            ofType(AssetActions.loadAssetData),
            concatLatestFrom(() => store$.select(selectSelectedSourceDataset)),
            switchMap(([action, dataset]) =>
                assetService.getAssetData(action.assetId, action.fundReportId, action.fundValuationId, action.dealId, dataset).pipe(
                    map((assetData) => AssetActions.loadAssetDataSuccess({assetData})),
                    catchError((errorResponse: any) => of(AssetActions.loadError({errorMsg: ErrorHandlingUtil.getErrorMessage(errorResponse)})))
                )
            )
        );
    },
    {functional: true}
);

export const archiveAsset = createEffect(
    (store$ = inject(Store), actions$ = inject(Actions), assetService = inject(AssetService)) => {
        return actions$.pipe(
            ofType(AssetActions.archive),
            concatLatestFrom(() => store$.select(selectAssetState)),
            exhaustMap(([action, state]) => {
                const selectedAsset: Asset = state.selectedAsset.data;
                if (selectedAsset.id) {
                    const assetId = selectedAsset.id;
                    return assetService.archiveAsset(assetId, action.archived).pipe(
                        map(asset => AssetActions.archiveSuccess({asset})),
                        catchError((errorResponse: any) => of(AssetActions.archiveError({errorMsg: ErrorHandlingUtil.getErrorMessage(errorResponse)})))
                    );
                } else {
                    return of(AssetActions.archiveError({errorMsg: "Asset could not be found"}));
                }
            })
        );
    },
    {functional: true}
);

export const archiveOverviewAssets = createEffect(
    (actions$ = inject(Actions), assetService = inject(AssetService)) => {
        return actions$.pipe(
            ofType(AssetOverviewActions.archiveAssets),
            exhaustMap((action) => {
                const assetsToUpdate = action.assets.filter((asset) => asset.id !== undefined);
                if (assetsToUpdate.length === 0) {
                    return of(AssetOverviewActions.archiveAssetsError({errorMsg: "No companies selected"}));
                }
                return forkJoin(
                    assetsToUpdate.map((asset) => toBulkResult(assetService.archiveAsset(asset.id!, action.archived)))
                ).pipe(
                    map((results) => {
                        const {count, failed, firstError} = summarizeBulkResults(results);
                        return count === 0
                            ? AssetOverviewActions.archiveAssetsError({errorMsg: firstError ?? "Companies could not be archived."})
                            : AssetOverviewActions.archiveAssetsSuccess({count, failed, archived: action.archived});
                    })
                );
            })
        );
    },
    {functional: true}
);

export const reloadAssetsAfterArchive = createEffect(
    (actions$ = inject(Actions)) => {
        return actions$.pipe(
            ofType(AssetOverviewActions.archiveAssetsSuccess),
            map(() => AssetOverviewActions.search())
        );
    },
    {functional: true}
);

export const showArchiveAssetsSuccess = createEffect(
    (actions$ = inject(Actions), snackBar = inject(MatSnackBar)) => {
        return actions$.pipe(
            ofType(AssetOverviewActions.archiveAssetsSuccess),
            tap((action) => {
                const noun = action.count === 1 ? "company" : "companies";
                const verb = action.archived ? "archived" : "unarchived";
                const failedSuffix = action.failed > 0 ? ` ${action.failed} failed.` : "";
                snackBar.open(`${action.count} ${noun} ${verb}.${failedSuffix}`, "", {duration: 3000});
            })
        );
    },
    {functional: true, dispatch: false}
);

export const showArchiveAssetsError = createEffect(
    (actions$ = inject(Actions), snackBar = inject(MatSnackBar)) => {
        return actions$.pipe(
            ofType(AssetOverviewActions.archiveAssetsError),
            tap((action) => {
                snackBar.open("Companies could not be archived. " + action.errorMsg, "", {duration: 3000});
            })
        );
    },
    {functional: true, dispatch: false}
);

export const saveAssetName = createEffect(
    (store$ = inject(Store), actions$ = inject(Actions), assetService = inject(AssetService)) => {
        return actions$.pipe(
            ofType(AssetNameActions.save),
            concatLatestFrom(() => store$.select(selectAssetState)),
            exhaustMap(([action, state]) => {
                const selectedAsset: Asset = state.selectedAsset.data;
                if (selectedAsset.id) {
                    const assetId = selectedAsset.id;
                    return assetService.saveAsset({
                        ...selectedAsset,
                        ...{
                            name: {
                                ...selectedAsset.name,
                                text: action.assetName
                            }
                        }
                    }, assetId).pipe(
                        map((asset) => AssetActions.saveLoadSuccess({asset})),
                        catchError((errorResponse: any) => of(AssetActions.loadError({errorMsg: ErrorHandlingUtil.getErrorMessage(errorResponse)})))
                    );
                } else {
                    return of(AssetActions.loadError({errorMsg: "Asset could not be found"}));
                }
            })
        );
    },
    {functional: true}
);

export const saveAssetDescription = createEffect(
    (store$ = inject(Store), actions$ = inject(Actions), assetService = inject(AssetService)) => {
        return actions$.pipe(
            ofType(AssetDescriptionActions.save),
            concatLatestFrom(() => store$.select(selectAssetState)),
            exhaustMap(([action, state]) => {
                const selectedAsset: Asset = state.selectedAsset.data;
                if (selectedAsset.id) {
                    const assetId = selectedAsset.id;
                    return assetService.saveAsset({
                        ...selectedAsset,
                        ...{
                            description: {
                                ...selectedAsset.description,
                                text: action.assetDescription
                            },
                            url: {
                                ...selectedAsset.url,
                                text: action.assetUrl
                            }
                        }
                    }, assetId).pipe(
                        map((asset) => AssetActions.saveLoadSuccess({asset})),
                        catchError((errorResponse: any) => of(AssetActions.loadError({errorMsg: ErrorHandlingUtil.getErrorMessage(errorResponse)})))
                    );
                } else {
                    return of(AssetActions.loadError({errorMsg: "Asset could not be found"}));
                }
            })
        );
    },
    {functional: true}
);

export const generateAssetDescription = createEffect(
    (store$ = inject(Store), actions$ = inject(Actions), assetService = inject(AssetService)) => {
        return actions$.pipe(
            ofType(AssetDescriptionActions.gptEdit),
            concatLatestFrom(() => [
                store$.select(selectAssetName),
                store$.select(selectAssetWebsiteUrl)
            ]),
            exhaustMap(([, selectedAssetName, websiteUrl]) => {
                const assetName = selectedAssetName.name.text;
                if (assetName) {
                    return assetService.getGptCompanyDescription(assetName, websiteUrl).pipe(
                        map((response) => AssetDescriptionActions.generateSuccess({description: response})),
                        catchError((errorResponse: any) => of(AssetDescriptionActions.generateFailure({errorMsg: ErrorHandlingUtil.getErrorMessage(errorResponse)})))
                    );
                } else {
                    return of(AssetDescriptionActions.generateFailure({errorMsg: "Asset description could not be generated, please enter a valid name for the asset first"}));
                }
            })
        );
    },
    {functional: true}
);

export const generateAssetGeneralInfo = createEffect(
    (store$ = inject(Store), actions$ = inject(Actions), assetService = inject(AssetService)) => {
        return actions$.pipe(
            ofType(AssetGeneralInformationActions.gptEdit),
            concatLatestFrom(() => [
                store$.select(selectAssetName),
                store$.select(selectAssetWebsiteUrl)
            ]),
            exhaustMap(([, assetNameState, websiteUrl]) => {
                const companyName = assetNameState.name.text;

                if (!companyName) {
                    return of(AssetGeneralInformationActions.gptGenerateFailure({
                        errorMsg: "Please enter a valid company name first"
                    }));
                }

                if (!websiteUrl) {
                    return of(AssetGeneralInformationActions.gptGenerateFailure({
                        errorMsg: "Please fill in the company website under Company Description first"
                    }));
                }

                return assetService.getGptGeneralInfo(companyName, websiteUrl).pipe(
                    map((generalInfo) => AssetGeneralInformationActions.gptGenerateSuccess({generalInfo})),
                    catchError((errorResponse: any) => {
                        let errorMsg: string;
                        const status = errorResponse?.status;

                        switch (status) {
                            case 400:
                                errorMsg = "Invalid request. Please check the company name and website URL.";
                                break;
                            case 401:
                            case 403:
                                errorMsg = "Authentication error. Please log in again.";
                                break;
                            case 404:
                                errorMsg = "Service not available. Please contact support.";
                                break;
                            case 408:
                                errorMsg = "Request timed out. Please try again.";
                                break;
                            case 429:
                                errorMsg = "Too many requests. Please wait a moment and try again.";
                                break;
                            case 500:
                            case 502:
                            case 503:
                            case 504:
                                errorMsg = "The AI service is temporarily unavailable. Please try again in a few minutes.";
                                break;
                            default:
                                errorMsg = "An unexpected error occurred. Please try again later.";
                        }

                        return of(AssetGeneralInformationActions.gptGenerateFailure({errorMsg}));
                    })
                );
            })
        );
    },
    {functional: true}
);

export const saveAssetGeneralInformation = createEffect(
    (store$ = inject(Store), actions$ = inject(Actions), assetService = inject(AssetService)) => {
        return actions$.pipe(
            ofType(AssetGeneralInformationActions.save),
            concatLatestFrom(() => store$.select(selectAssetState)),
            exhaustMap(([action, state]) => {
                const selectedAsset: Asset = state.selectedAsset.data;
                if (selectedAsset.id) {
                    const assetId = selectedAsset.id;
                    return assetService.saveAsset({
                        ...selectedAsset,
                        ...{
                            country: {
                                ...selectedAsset.country,
                                code: action.country ?? undefined
                            },
                            activityDescription: {
                                ...selectedAsset.activityDescription,
                                text: action.activityDescription ?? undefined
                            },
                            regions: action.regions,
                            stage: {
                                ...selectedAsset.stage,
                                code: action.stage
                            },
                            gic: {
                                ...selectedAsset.gic,
                                code: action.gic ?? undefined
                            },
                            currencyIso: {
                                ...selectedAsset.currencyIso,
                                code: action.currency
                            },
                            esg: {
                                ...selectedAsset.esg,
                                code: action.esg
                            },
                            fiscalYearEnd: {
                                ...selectedAsset.fiscalYearEnd,
                                code: action.fiscalYearEnd
                            },
                            assessment: {
                                ...selectedAsset.assessment,
                                code: action.assessment
                            },
                            publiclyListed: action.publiclyListed
                        }
                    }, assetId).pipe(
                        map((asset) => AssetActions.saveLoadSuccess({asset})),
                        catchError((errorResponse: any) => of(AssetActions.loadError({errorMsg: ErrorHandlingUtil.getErrorMessage(errorResponse)})))
                    );
                } else {
                    return of(AssetActions.loadError({errorMsg: "Error while loading the asset, please refresh the page"}));
                }
            })
        );
    },
    {functional: true}
);

export const generatePeriodDatesAfterAssetLoadOrSave = createEffect(
    (actions$ = inject(Actions)) => {
        return actions$.pipe(
            ofType(AssetActions.loadAssetDataSuccess, AssetActions.saveLoadSuccess),
            map(() => AssetActions.generatePeriodDates()));
    },
    {functional: true}
);

export const generatedPeriodDates = createEffect(
    (store$ = inject(Store), actions$ = inject(Actions)) => {
        return actions$.pipe(
            ofType(AssetActions.generatePeriodDates),
            concatLatestFrom(() => store$.select(selectAssetFiscalYearEnd)),
            map(([, fiscalYearEndMonth]) => {
                const availablePeriodDates = PeriodUtil.generateAvailablePeriodDates(fiscalYearEndMonth);
                return AssetActions.generatePeriodDatesSuccess({availablePeriodDates});
            }));
    },
    {functional: true}
);

export const loadGeneralPartnerValuation = createEffect(
    (actions$ = inject(Actions), fundInvestmentService = inject(FundInvestmentService)) => {
        return actions$.pipe(
            ofType(GeneralPartnerValuationActions.load),
            exhaustMap((action) => {
                if (!!action.fundId && !!action.fundReportId && !!action.fundInvestmentId) {
                    return fundInvestmentService.getFundInvestment(action.fundId, action.fundReportId, action.fundInvestmentId).pipe(
                        map((fundInvestment) => GeneralPartnerValuationActions.loadSuccess({fundInvestment})),
                        catchError((errorResponse: any) => of(GeneralPartnerValuationActions.loadError({errorMsg: ErrorHandlingUtil.getErrorMessage(errorResponse)})))
                    );
                } else {
                    return of(GeneralPartnerValuationActions.loadError({errorMsg: "Error while loading the gp valuation, please refresh the page"}));
                }
            })
        );
    },
    {functional: true}
);

export const saveGeneralPartnerValuation = createEffect(
    (store$ = inject(Store), actions$ = inject(Actions), fundInvestmentService = inject(FundInvestmentService)) => {
        return actions$.pipe(
            ofType(GeneralPartnerValuationActions.save),
            concatLatestFrom(() => [
                store$.select(selectRelatedFund),
                store$.select(selectRelatedReportId),
                store$.select(selectRelatedFundValuationId)
            ]),
            exhaustMap(([action, fund, fundReportId, fundValuationId]) => {
                if (!!fund && !!fund.id && !!fundReportId && !!action.fundInvestment.id) {
                    return fundInvestmentService.updateFundInvestment(fund.id, fundReportId, action.fundInvestment.id, action.fundInvestment, fundValuationId).pipe(
                        map((fundInvestmentResponse) => GeneralPartnerValuationActions.saveLoadSuccess({fundInvestment: fundInvestmentResponse})),
                        catchError((errorResponse: any) => of(GeneralPartnerValuationActions.loadError({errorMsg: ErrorHandlingUtil.getErrorMessage(errorResponse)})))
                    );
                } else {
                    return of(GeneralPartnerValuationActions.loadError({errorMsg: "Error while loading the fund investment, please refresh the page"}));
                }
            })
        );
    },
    {functional: true}
);

export const calcGeneralPartnerValuation = createEffect(
    (store$ = inject(Store), actions$ = inject(Actions), fundInvestmentService = inject(FundInvestmentService)) => {
        return actions$.pipe(
            ofType(GeneralPartnerValuationActions.calc),
            concatLatestFrom(() => [
                store$.select(selectRelatedFund),
                store$.select(selectRelatedReportId),
                store$.select(selectGeneralPartnerValuation)
            ]),
            exhaustMap(([action, fund, fundReportId, fundInvestment]) => {
                if (fund?.id && fundReportId && fundInvestment.data.id) {
                    return fundInvestmentService.calcFundInvestment(fund.id, fundReportId, fundInvestment.data.id, {
                        ...fundInvestment.data,
                        gpIndication: {
                            ...fundInvestment.data.gpIndication,
                            code: action.gpIndication
                        },
                        remainingCost: {
                            ...fundInvestment.data.remainingCost,
                            amount: action.remainingCost
                        },
                        mezzanine: {
                            ...fundInvestment.data.mezzanine,
                            amount: action.mezzanine
                        },
                        preferredEquity: {
                            ...fundInvestment.data.preferredEquity,
                            amount: action.preferredEquity
                        },
                        commonEquity: {
                            ...fundInvestment.data.commonEquity,
                            amount: action.commonEquity
                        },
                        realizedCost: {
                            ...fundInvestment.data.realizedCost,
                            amount: action.realizedCost
                        },
                        realizedGains: {
                            ...fundInvestment.data.realizedGains,
                            amount: action.realizedGains
                        },
                        committedInvestment: {
                            ...fundInvestment.data.committedInvestment,
                            amount: action.committedInvestment
                        }
                    }).pipe(
                        map((fundInvestmentResponse) => GeneralPartnerValuationActions.calcSuccess({fundInvestment: fundInvestmentResponse})),
                        catchError((errorResponse: any) => of(GeneralPartnerValuationActions.loadError({errorMsg: ErrorHandlingUtil.getErrorMessage(errorResponse)})))
                    );
                } else {
                    return of(GeneralPartnerValuationActions.loadError({errorMsg: "Error while loading the fund investment, please refresh the page"}));
                }
            })
        );
    },
    {functional: true}
);

export const saveFundInvestmentRealized = createEffect(
    (store$ = inject(Store), actions$ = inject(Actions), fundInvestmentService = inject(FundInvestmentService)) => {
        return actions$.pipe(
            ofType(GeneralPartnerValuationActions.saveRealization),
            concatLatestFrom(() => [
                store$.select(selectRelatedFund),
                store$.select(selectRelatedReportId),
                store$.select(selectRelatedFundValuationId),
                store$.select(selectGeneralPartnerId)
            ]),
            exhaustMap(([action, fund, fundReportId, fundValuationId, fundInvestmentId]) => {
                if (!!fund && !!fund.id && !!fundReportId && !!fundInvestmentId) {
                    return fundInvestmentService.updateFundInvestmentRealization(
                        fund.id,
                        fundReportId,
                        fundInvestmentId,
                        action.isRealized,
                        action.investmentDate,
                        action.exitDate,
                        fundValuationId
                    ).pipe(
                        map((fundInvestment) => GeneralPartnerValuationActions.saveRealizationSuccess({fundInvestment})),
                        catchError((errorResponse: any) => of(GeneralPartnerValuationActions.loadError({errorMsg: ErrorHandlingUtil.getErrorMessage(errorResponse)})))
                    );
                } else {
                    return of(GeneralPartnerValuationActions.loadError({errorMsg: "Error while loading the fund investment, please refresh the page"}));
                }
            })
        );
    },
    {functional: true}
);

export const saveSwot = createEffect(
    (store$ = inject(Store), actions$ = inject(Actions), assetService = inject(AssetService)) => {
        return actions$.pipe(
            ofType(AssetSwotActions.save),
            concatLatestFrom(() => store$.select(selectAssetState)),
            exhaustMap(([action, state]) => {
                const selectedAsset: Asset = state.selectedAsset.data;
                if (selectedAsset.id) {
                    const assetId = selectedAsset.id;
                    return assetService.saveAsset({
                        ...selectedAsset,
                        ...{
                            swot: {
                                ...selectedAsset.swot,
                                ...{
                                    swotStrengths: {
                                        ...selectedAsset.swot.swotStrengths,
                                        text: action.swotStrengths
                                    },
                                    swotWeaknesses: {
                                        ...selectedAsset.swot.swotWeaknesses,
                                        text: action.swotWeaknesses
                                    },
                                    swotOpportunities: {
                                        ...selectedAsset.swot.swotOpportunities,
                                        text: action.swotOpportunities
                                    },
                                    swotThreats: {
                                        ...selectedAsset.swot.swotThreats,
                                        text: action.swotThreats
                                    }
                                }
                            }
                        }
                    }, assetId).pipe(
                        map((asset) => AssetActions.saveLoadSuccess({asset})),
                        catchError((errorResponse: any) => of(AssetActions.loadError({errorMsg: ErrorHandlingUtil.getErrorMessage(errorResponse)})))
                    );
                } else {
                    return of(AssetActions.loadError({errorMsg: "Error while loading the asset, please refresh the page"}));
                }

            })
        );
    },
    {functional: true}
);

export const generateSwotAnalysis = createEffect(
    (store$ = inject(Store), actions$ = inject(Actions), assetService = inject(AssetService)) => {
        return actions$.pipe(
            ofType(AssetSwotActions.gptEdit),
            concatLatestFrom(() => [
                store$.select(selectAssetName),
                store$.select(selectAssetWebsiteUrl)
            ]),
            exhaustMap(([, selectedAssetName, websiteUrl]) => {
                const assetName = selectedAssetName.name.text;
                if (assetName) {
                    return assetService.getGptSwotAnalysis(assetName, websiteUrl).pipe(
                        map((response) => AssetSwotActions.generateSuccess({swot: response})),
                        catchError((errorResponse: any) => of(AssetSwotActions.generateFailure({errorMsg: ErrorHandlingUtil.getErrorMessage(errorResponse)})))
                    );
                } else {
                    return of(AssetSwotActions.generateFailure({errorMsg: "Asset SWOT could not be generated, please enter a valid name for the asset first"}));
                }
            })
        );
    },
    {functional: true}
);

export const saveEndMarketsCustomers = createEffect(
    (store$ = inject(Store), actions$ = inject(Actions), assetService = inject(AssetService)) => {
        return actions$.pipe(
            ofType(EndMarketsCustomersActions.save),
            concatLatestFrom(() => store$.select(selectSelectedAsset)),
            exhaustMap(([action, asset]) => {
                const selectedAsset: Asset = asset.data;
                if (selectedAsset.id) {
                    const assetId = selectedAsset.id;
                    return assetService.saveAsset({
                        ...selectedAsset,
                        ...{
                            endMarketsCustomers: {
                                ...selectedAsset.endMarketsCustomers,
                                ...{
                                    text: action.text
                                }
                            }
                        }
                    }, assetId).pipe(
                        map((updatedAsset) => AssetActions.saveLoadSuccess({asset: updatedAsset})),
                        catchError((errorResponse: any) => of(AssetActions.loadError({errorMsg: ErrorHandlingUtil.getErrorMessage(errorResponse)})))
                    );
                } else {
                    return of(AssetActions.loadError({errorMsg: "Error while loading the asset, please refresh the page"}));
                }
            })
        );
    },
    {functional: true}
);

export const saveDevelopmentsSinceInvestment = createEffect(
    (store$ = inject(Store), actions$ = inject(Actions), fundInvestmentService = inject(FundInvestmentService)) => {
        return actions$.pipe(
            ofType(DevelopmentsSinceInvestmentActions.save),
            concatLatestFrom(() => [
                store$.select(selectGeneralPartnerValuation),
                store$.select(selectRelatedFund),
                store$.select(selectRelatedReportId),
                store$.select(selectSelectedAssetId)
            ]),
            exhaustMap(([action, fundInvestment, fund, fundReportId, assetId]) => {
                const currentFundInvestment = fundInvestment.data;
                if (fund?.id && fundReportId && currentFundInvestment.id) {
                    return fundInvestmentService.updateFundInvestmentDevelopmentsSinceInvestment(fund.id, fundReportId, currentFundInvestment.id, action.text).pipe(
                        // The save updates only the generalPartnerValuation slice, not the
                        // commentaryHistory read-model (loaded once per asset). Reload it
                        // so the just-saved entry isn't stale once its row is no longer the current report.
                        switchMap((fundInvestmentResponse) => assetId
                            ? of(
                                DevelopmentsSinceInvestmentActions.saveSuccess({fundInvestment: fundInvestmentResponse}),
                                AssetCommentaryHistoryActions.load({assetId})
                            )
                            : of(DevelopmentsSinceInvestmentActions.saveSuccess({fundInvestment: fundInvestmentResponse}))),
                        // This editor's own error, not GeneralPartnerValuationActions.loadError, which
                        // blanks the record — that is what made the commentary vanish on a failed save.
                        catchError((errorResponse: any) => of(DevelopmentsSinceInvestmentActions.saveError({errorMsg: ErrorHandlingUtil.getErrorMessage(errorResponse)})))
                    );
                } else {
                    return of(DevelopmentsSinceInvestmentActions.saveError({errorMsg: "Error while loading the fund investment, please refresh the page"}));
                }
            })
        );
    },
    {functional: true}
);

// One effect for both cards. switchMap, not exhaustMap: navigating between assets must abandon the
// in-flight request for the previous asset rather than ignore the new one.
export const loadAssetCommentaryHistory = createEffect(
    (actions$ = inject(Actions), assetService = inject(AssetService)) => {
        return actions$.pipe(
            ofType(AssetCommentaryHistoryActions.load),
            switchMap((action) => {
                return assetService.getCommentaryHistory(action.assetId).pipe(
                    map((history) => AssetCommentaryHistoryActions.loadSuccess({history})),
                    catchError((errorResponse: any) => of(AssetCommentaryHistoryActions.loadError({errorMsg: ErrorHandlingUtil.getErrorMessage(errorResponse)})))
                );
            })
        );
    },
    {functional: true}
);

export const saveRecentFinancialPerformance = createEffect(
    (store$ = inject(Store), actions$ = inject(Actions), fundInvestmentService = inject(FundInvestmentService)) => {
        return actions$.pipe(
            ofType(RecentFinancialPerformanceActions.save),
            concatLatestFrom(() => [
                store$.select(selectGeneralPartnerValuation),
                store$.select(selectRelatedFund),
                store$.select(selectRelatedReportId),
                store$.select(selectSelectedAssetId)
            ]),
            exhaustMap(([action, fundInvestment, fund, fundReportId, assetId]) => {
                const currentFundInvestment = fundInvestment.data;
                if (fund?.id && fundReportId && currentFundInvestment.id) {
                    return fundInvestmentService.updateFundInvestmentRecentFinancialPerformance(fund.id, fundReportId, currentFundInvestment.id, action.text).pipe(
                        // The save updates only the generalPartnerValuation slice, not the
                        // commentaryHistory read-model (loaded once per asset). Reload it
                        // so the just-saved entry isn't stale once its row is no longer the current report.
                        switchMap((fundInvestmentResponse) => assetId
                            ? of(
                                RecentFinancialPerformanceActions.saveSuccess({fundInvestment: fundInvestmentResponse}),
                                AssetCommentaryHistoryActions.load({assetId})
                            )
                            : of(RecentFinancialPerformanceActions.saveSuccess({fundInvestment: fundInvestmentResponse}))),
                        // This editor's own error, not GeneralPartnerValuationActions.loadError, which
                        // blanks the record — that is what made the commentary vanish on a failed save.
                        catchError((errorResponse: any) => of(RecentFinancialPerformanceActions.saveError({errorMsg: ErrorHandlingUtil.getErrorMessage(errorResponse)})))
                    );
                } else {
                    return of(RecentFinancialPerformanceActions.saveError({errorMsg: "Error while loading the fund investment, please refresh the page"}));
                }
            })
        );
    },
    {functional: true}
);


export const saveTransactionSummary = createEffect(
    (store$ = inject(Store), actions$ = inject(Actions), fundInvestmentService = inject(FundInvestmentService)) => {
        return actions$.pipe(
            ofType(TransactionSummaryActions.save),
            concatLatestFrom(() => [
                store$.select(selectGeneralPartnerValuation),
                store$.select(selectRelatedFund),
                store$.select(selectRelatedReportId)
            ]),
            exhaustMap(([action, fundInvestment, fund, fundReportId]) => {
                const currentFundInvestment = fundInvestment.data;
                if (fund?.id && fundReportId && currentFundInvestment.id) {
                    // Narrow PATCH, matching the recentFinancialPerformance/developmentsSinceInvestment
                    // editors. The previous full-object PUT sent the whole store snapshot plus
                    // relatedFundValuationId, so it clobbered anything saved since that snapshot was
                    // taken, failed outright when the realization save had just deleted the valuation
                    // that id pointed at, and re-ran the fund-report calculation a second time.
                    return fundInvestmentService.updateFundInvestmentTransactionSummary(fund.id, fundReportId, currentFundInvestment.id, action.text).pipe(
                        map((fundInvestmentResponse) => TransactionSummaryActions.saveSuccess({fundInvestment: fundInvestmentResponse})),
                        // This editor's own error, not GeneralPartnerValuationActions.loadError, which
                        // blanks the record — that is what made the saved text vanish on a failed save.
                        catchError((errorResponse: any) => of(TransactionSummaryActions.saveError({errorMsg: ErrorHandlingUtil.getErrorMessage(errorResponse)})))
                    );
                } else {
                    return of(TransactionSummaryActions.saveError({errorMsg: "Error while loading the fund investment, please refresh the page"}));
                }
            })
        );
    },
    {functional: true}
);

export const generateEndMarketsDescription = createEffect(
    (store$ = inject(Store), actions$ = inject(Actions), assetService = inject(AssetService)) => {
        return actions$.pipe(
            ofType(EndMarketsCustomersActions.gptEdit),
            concatLatestFrom(() => [
                store$.select(selectAssetName),
                store$.select(selectAssetWebsiteUrl)
            ]),
            exhaustMap(([, selectedAssetName, websiteUrl]) => {
                const assetName = selectedAssetName.name.text;
                if (assetName) {
                    return assetService.getGptEndMarkets(assetName, websiteUrl).pipe(
                        map((response) => EndMarketsCustomersActions.generateSuccess({description: response})),
                        catchError((errorResponse: any) => of(EndMarketsCustomersActions.generateFailure({errorMsg: ErrorHandlingUtil.getErrorMessage(errorResponse)})))
                    );
                } else {
                    return of(EndMarketsCustomersActions.generateFailure(
                        {errorMsg: "Asset end markets and customers could not be generated, please enter a valid name for the asset first"}
                    ));
                }
            })
        );
    },
    {functional: true}
);

export const loadAssetFinancialHistory = createEffect(
    (actions$ = inject(Actions), assetService = inject(AssetService)) => {
        return actions$.pipe(
            ofType(AssetFinancialHistoryActions.load),
            exhaustMap((action) =>
                assetService.getAssetFinancialHistory(action.assetId).pipe(
                    map((financialHistory) => AssetFinancialHistoryActions.loadSuccess({financialHistory})),
                    catchError((errorResponse: any) => of(AssetFinancialHistoryActions.loadError({errorMsg: ErrorHandlingUtil.getErrorMessage(errorResponse)})))
                )
            )
        );
    },
    {functional: true}
);

export const loadFinancialHistoryAfterCancel = createEffect(
    (store$ = inject(Store), actions$ = inject(Actions)) => {
        return actions$.pipe(
            ofType(AssetFinancialHistoryActions.cancel),
            concatLatestFrom(() => store$.select(selectSelectedAssetId)),
            map(([, assetId]) =>
                assetId
                    ? AssetFinancialHistoryActions.load({assetId})
                    : AssetFinancialHistoryActions.loadError({errorMsg: "Error while loading the asset, please refresh the page"})
            ));
    },
    {functional: true}
);

export const saveAssetFinancialHistory = createEffect(
    (store$ = inject(Store), actions$ = inject(Actions), assetService = inject(AssetService)) => {
        return actions$.pipe(
            ofType(AssetFinancialHistoryActions.save),
            concatLatestFrom(() => store$.select(selectSelectedAssetId)),
            exhaustMap(([action, assetId]) => {
                if (assetId === undefined) {
                    return of(AssetFinancialHistoryActions.loadError({errorMsg: "Error while loading the asset, please refresh the page"}));
                }
                return assetService.saveAssetFinancialHistory(assetId, action.financialHistory).pipe(
                    map((financialHistory) => AssetFinancialHistoryActions.saveLoadSuccess({financialHistory})),
                    catchError((errorResponse: any) => of(AssetFinancialHistoryActions.loadError({errorMsg: ErrorHandlingUtil.getErrorMessage(errorResponse)})))
                );
            })
        );
    },
    {functional: true}
);

export const reloadAllFinancialPerformanceRecordsAfterSavingFinHistory = createEffect(
    (actions$ = inject(Actions), store$ = inject(Store)) => {
        return actions$.pipe(
            ofType(AssetFinancialHistoryActions.saveLoadSuccess, AssetValuationDetailActions.saveSuccess),
            concatLatestFrom(() => [
                store$.select(selectSelectedAssetId),
                store$.select(selectActiveAssetValuation),
                store$.select(selectActiveScenarios)
            ]),
            filter(([, , assetValuation]) =>
                assetValuation.data?.valuationType?.code === AssetValuationTypeEnum.FinancialOperationalValuation
            ),
            mergeMap(([, assetId, assetValuation, fovScenarios]) => {
                const actions = fovScenarios.map(scenario => {
                    if (assetId && assetValuation.data.id && scenario.id && scenario.scenario.code && isOfScenarioType(scenario.scenario.code)) {
                        return FinancialPerformanceRecordsActions.load({
                            assetId,
                            assetValuationId: assetValuation.data.id,
                            scenarioId: scenario.id,
                            scenarioType: scenario.scenario.code
                        })
                    }
                    return FinancialPerformanceRecordsActions.doNothing();
                });

                return from(actions);
            })
        );
    },
    {functional: true}
);

export const reloadFovAfterSavingFinHistory = createEffect(
    (actions$ = inject(Actions), store$ = inject(Store)) => {
        return actions$.pipe(
            ofType(AssetFinancialHistoryActions.saveLoadSuccess),
            concatLatestFrom(() => [store$.select(selectSelectedAssetId), store$.select(selectRelatedFundValuationId)]),
            map(([, assetId, fundValuationId]) => {
                if (assetId === undefined || fundValuationId === undefined) {
                    return AssetValuationActions.doNothing();
                }
                return AssetValuationActions.loadAllForAssetAndFundValuation({assetId, fundValuationId});
            })
        );
    },
    {functional: true}
);

export const calcAssetFinancialHistory = createEffect(
    (store$ = inject(Store), actions$ = inject(Actions), assetService = inject(AssetService)) => {
        return actions$.pipe(
            ofType(AssetFinancialHistoryActions.calc),
            concatLatestFrom(() => store$.select(selectSelectedAssetId)),
            exhaustMap(([action, assetId]) => {
                if (assetId === undefined) {
                    return of(AssetFinancialHistoryActions.calcError({errorMsg: "Error while loading the asset, please refresh the page"}));
                }
                return assetService.calcAssetFinancialHistory(assetId, action.financialHistoryRecord).pipe(
                    map((calculatedFinancialHistory) => AssetFinancialHistoryActions.calcSuccess({financialHistory: calculatedFinancialHistory})),
                    catchError((errorResponse: any) => of(AssetFinancialHistoryActions.calcError({errorMsg: ErrorHandlingUtil.getErrorMessage(errorResponse)})))
                );
            })
        );
    },
    {functional: true}
);

export const loadAllShareholders = createEffect(
    (actions$ = inject(Actions), assetService = inject(AssetService)) => {
        return actions$.pipe(
            ofType(ShareholderActions.load),
            exhaustMap((action) => {
                return assetService.getAllShareholders(action.assetId).pipe(
                    map((shareholders) => ShareholderActions.loadAllSuccess({shareholders})),
                    catchError((errorResponse: any) => of(ShareholderActions.loadError({errorMsg: ErrorHandlingUtil.getErrorMessage(errorResponse)})))
                );
            })
        );
    },
    {functional: true}
);

export const loadShareholdersForDate = createEffect(
    (actions$ = inject(Actions), assetService = inject(AssetService)) => {
        return actions$.pipe(
            ofType(ShareholderActions.load),
            switchMap((action) => {
                return assetService.getShareholdersForDate(action.assetId, action.date).pipe(
                    map((shareholders) => ShareholderActions.loadSuccess({shareholders})),
                    catchError((errorResponse: any) => of(ShareholderActions.loadError({errorMsg: ErrorHandlingUtil.getErrorMessage(errorResponse)})))
                );
            })
        );
    },
    {functional: true}
);


export const saveShareholdersForDate = createEffect(
    (store$ = inject(Store), actions$ = inject(Actions), assetService = inject(AssetService)) => {
        return actions$.pipe(
            ofType(ShareholderActions.save),
            exhaustMap((action) => {
                if (action.assetId === undefined || !action.date) {
                    return of(ShareholderActions.saveError({errorMsg: "Shareholder could not be saved, enter a valid date first"}));
                }
                return assetService.saveShareholdersForDate(action.assetId, action.date, action.shareholders).pipe(
                    map((shareholders) => ShareholderActions.saveLoadSuccess({shareholders})),
                    catchError((errorResponse: any) => of(ShareholderActions.saveError({errorMsg: ErrorHandlingUtil.getErrorMessage(errorResponse)})))
                );
            })
        );
    },
    {functional: true}
);

export const reloadShareholdersAfterRealizationSave = createEffect(
    (actions$ = inject(Actions), store$ = inject(Store)) => {
        return actions$.pipe(
            ofType(GeneralPartnerValuationActions.saveRealizationSuccess),
            concatLatestFrom(() => store$.select(selectReportDate)),
            filter(([{fundInvestment}, reportDate]) => !!fundInvestment?.assetId && !!reportDate?.date),
            map(([{fundInvestment}, reportDate]) => {
                const date = reportDate!.date as string;
                return ShareholderActions.load({
                    assetId: fundInvestment.assetId,
                    date
                });
            })
        );
    },
    {functional: true}
);

export const setMergeTargetId = createEffect(
    (store$ = inject(Store), actions$ = inject(Actions), assetService = inject(AssetService)) => {
        return actions$.pipe(
            ofType(AssetActions.setMergeTarget),
            concatLatestFrom(() => [
                store$.select(selectSelectedAssetId),
                store$.select(selectSelectedFundReportId),
                store$.select(selectSelectedSourceDataset)
            ]),
            exhaustMap(([action, assetId, fundReportId, dataset]) => {
                if (assetId) {
                    return assetService.setMergeTarget(assetId, fundReportId, action.mergeTargetId, dataset).pipe(
                        map((assetData) => AssetActions.setMergeTargetSuccess({assetData})),
                        catchError((errorResponse: any) => of(AssetActions.setMergeTargetError({errorMsg: ErrorHandlingUtil.getErrorMessage(errorResponse)})))
                    );
                } else {
                    return of(AssetActions.setMergeTargetError({errorMsg: "Asset could not be found"}));
                }
            })
        );
    },
    {functional: true}
);
